// Client-side share-card rendering (§5.5). Canvas-based (no dependency), sized
// 1080×1350 for feeds, watermarked twoshot.xyz. Two units: the score card and a
// dispute head-to-head. Deterministic given the same inputs.

const W = 1080;
const H = 1350;

const COLORS = {
  // v2: Oxblood & Apricot (dark ink share card)
  bg: "#1F1114",
  panel: "#2E1B20",
  cream: "#F9F0E5",
  muted: "#8D7178",
  orange: "#E8945A", // apricot
  teal: "#E8945A",
  pink: "#75182A", // oxblood
};

function tierColor(score: number): string {
  if (score >= 85) return COLORS.teal;
  if (score >= 70) return COLORS.orange;
  if (score >= 50) return COLORS.pink;
  return COLORS.muted;
}

function hashColor(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360} 40% 26%)`;
}

function makeCanvas(): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  return c;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function header(ctx: CanvasRenderingContext2D) {
  // Logo dots + wordmark.
  ctx.fillStyle = COLORS.orange;
  ctx.beginPath();
  ctx.arc(88, 92, 16, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = COLORS.teal;
  ctx.beginPath();
  ctx.arc(108, 92, 16, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = COLORS.cream;
  ctx.font = "700 40px Inter, system-ui, sans-serif";
  ctx.textBaseline = "middle";
  ctx.fillText("twoshot", 140, 94);
}

function watermark(ctx: CanvasRenderingContext2D) {
  ctx.fillStyle = COLORS.muted;
  ctx.font = "500 32px Inter, system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText("twoshot.xyz", W / 2, H - 70);
  ctx.textAlign = "left";
}

export interface ScoreCardData {
  nameA: string;
  nameB: string;
  score: number;
  tier: string;
  canon: { title: string; year?: number; uri: string }[];
}

export function renderScoreCard(data: ScoreCardData): HTMLCanvasElement {
  const c = makeCanvas();
  const ctx = c.getContext("2d")!;
  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, W, H);
  header(ctx);

  // Names.
  ctx.textAlign = "center";
  ctx.fillStyle = COLORS.cream;
  ctx.font = "700 72px Inter, system-ui, sans-serif";
  ctx.fillText(`${data.nameA} × ${data.nameB}`, W / 2, 300);

  // Score dial.
  const cx = W / 2;
  const cy = 560;
  const r = 150;
  ctx.lineWidth = 24;
  ctx.strokeStyle = COLORS.panel;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.strokeStyle = tierColor(data.score);
  ctx.beginPath();
  ctx.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + (Math.PI * 2 * data.score) / 100);
  ctx.lineCap = "round";
  ctx.stroke();
  ctx.fillStyle = COLORS.cream;
  ctx.font = "700 130px Inter, system-ui, sans-serif";
  ctx.textBaseline = "middle";
  ctx.fillText(String(data.score), cx, cy + 6);

  // Tier label.
  ctx.fillStyle = tierColor(data.score);
  ctx.font = "600 46px Inter, system-ui, sans-serif";
  ctx.fillText(data.tier, W / 2, 800);

  // Shared-canon posters (up to 3).
  const posters = data.canon.slice(0, 3);
  const pw = 240;
  const ph = 360;
  const gap = 40;
  const totalW = posters.length * pw + (posters.length - 1) * gap;
  let x = (W - totalW) / 2;
  const y = 900;
  ctx.textAlign = "left";
  for (const p of posters) {
    ctx.fillStyle = hashColor(`${p.title}|${p.year}`);
    roundRect(ctx, x, y, pw, ph, 16);
    ctx.fill();
    ctx.fillStyle = COLORS.cream;
    ctx.font = "600 26px Inter, system-ui, sans-serif";
    wrapText(ctx, p.title, x + 16, y + ph - 60, pw - 32, 30);
    x += pw + gap;
  }
  ctx.textAlign = "center";
  ctx.fillStyle = COLORS.muted;
  ctx.font = "500 30px Inter, system-ui, sans-serif";
  ctx.fillText("What we both love", W / 2, y + ph + 60);

  watermark(ctx);
  ctx.textAlign = "left";
  return c;
}

export interface DisputeCardData {
  nameA: string;
  nameB: string;
  title: string;
  year?: number;
  ratingA: number;
  ratingB: number;
}

export function renderDisputeCard(d: DisputeCardData): HTMLCanvasElement {
  const c = makeCanvas();
  const ctx = c.getContext("2d")!;
  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, W, H);
  header(ctx);

  ctx.textAlign = "center";
  ctx.fillStyle = COLORS.muted;
  ctx.font = "600 40px Inter, system-ui, sans-serif";
  ctx.fillText("THE DISPUTE", W / 2, 260);

  ctx.fillStyle = COLORS.cream;
  ctx.font = "700 76px Inter, system-ui, sans-serif";
  wrapCentered(ctx, d.title, W / 2, 380, W - 160, 84);
  ctx.fillStyle = COLORS.muted;
  ctx.font = "500 40px Inter, system-ui, sans-serif";
  ctx.fillText(String(d.year ?? ""), W / 2, 520);

  // Two big ratings.
  const drawSide = (name: string, rating: number, color: string, cx: number) => {
    ctx.fillStyle = color;
    ctx.font = "700 160px Inter, system-ui, sans-serif";
    ctx.fillText(`★${rating.toFixed(1)}`, cx, 800);
    ctx.fillStyle = COLORS.cream;
    ctx.font = "600 44px Inter, system-ui, sans-serif";
    ctx.fillText(name, cx, 920);
  };
  drawSide(d.nameA, d.ratingA, COLORS.orange, W * 0.3);
  drawSide(d.nameB, d.ratingB, COLORS.teal, W * 0.7);

  ctx.fillStyle = COLORS.muted;
  ctx.font = "500 40px Inter, system-ui, sans-serif";
  ctx.fillText("vs", W / 2, 780);

  watermark(ctx);
  ctx.textAlign = "left";
  return c;
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, maxW: number, lh: number) {
  const words = text.split(" ");
  let line = "";
  const lines: string[] = [];
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxW && line) {
      lines.push(line);
      line = w;
    } else line = test;
  }
  if (line) lines.push(line);
  const start = y - (lines.length - 1) * lh;
  lines.forEach((l, i) => ctx.fillText(l, x, start + i * lh));
}

function wrapCentered(ctx: CanvasRenderingContext2D, text: string, cx: number, y: number, maxW: number, lh: number) {
  const words = text.split(" ");
  let line = "";
  const lines: string[] = [];
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxW && line) {
      lines.push(line);
      line = w;
    } else line = test;
  }
  if (line) lines.push(line);
  lines.forEach((l, i) => ctx.fillText(l, cx, y + i * lh));
}

/** Deliver a canvas per §4: native share sheet on mobile, download on desktop. */
export async function deliverCard(canvas: HTMLCanvasElement, filename: string) {
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
  if (!blob) return;
  const file = new File([blob], filename, { type: "image/png" });
  if (typeof navigator !== "undefined" && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: "Our twoshot blend" });
      return;
    } catch {
      /* fall through to download */
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
